/* eslint-disable max-lines */
/* global window */
import httpStatusCodes from 'http-status-codes';
import * as urlHelper from 'Helpers/url';
import cookieManagerHelper from 'Helpers/cookies';
import { getNotificationInstallationId } from 'Helpers/pushNotification';
import { getLanguageBasePath } from 'Helpers/routeHelper';
import * as parser from 'Helpers/parser';
import { smartlockHelper } from 'Helpers/authentication';
import ChatModule from './asyncChat';
import { LOCATION_ONBOARDING_STATUS } from 'Components/LocationOnboarding/constants';
import { EvaluationConstants } from 'olx-autos-landing-page';
import { removeItem } from 'Helpers/sessionStorage';
import { IS_SELLER_KYC_POPUP_SHOWN } from 'ragnarok-web/dist/Constants/constants';
import { clearLeadsDataFromStorage } from 'Helpers/item';
import { clearEmiSelectedAdsFromStorage } from 'Helpers/priceCardTabs';

const loginAPI = '/api/account';
const logoutAPI = '/api/account';
const legionLogoutAPI = '/api/auth/deauthorize';

const transformUserData = response => {
    const userID = response.data.user_id;

    return {
        info: response.data,
        metadata: response.metadata && response.metadata.users && response.metadata.users[userID]
    };
};
const DEFAULT_CALLBACK = args => args;

const KEYS_TO_PRESERVE = ['device_fingerprint', LOCATION_ONBOARDING_STATUS];
const KEYS_TO_PRESERVE_REGEX = [/WZRK_/];
const COOKIES_TO_CLEAR = [EvaluationConstants.SELLER_STATE_COOKIE, EvaluationConstants.REPEAT_USER_COOKIE_NAME];

class Auth {
    static user = null;
    static sendAccountDetails(accountDetails, callback = DEFAULT_CALLBACK) {
        let res = {
            data: null,
            status: null,
            error: null
        };
        const options = {
            method: 'POST',
            body: JSON.stringify(accountDetails),
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            }
        };

        const apiEndpoint = loginAPI;

        return fetch(apiEndpoint, options)
            .then(response => {
                if (response.status === httpStatusCodes.OK) {
                    return response.json().then(json => {
                        res = {
                            data: transformUserData(json),
                            status: httpStatusCodes.OK
                        };
                        const data = json.data;

                        if (typeof window !== 'undefined') {
                            if (data && data.chat_token && window.localStorage) {
                                return this.setChatToken(data.chat_token, data.user_id, () => {
                                    callback(res);
                                });
                            }
                            else if (window.newrelic && accountDetails.grant_type !== 'refresh_token') {
                                window.newrelic.addPageAction('chat_login_error', { response: data, accountDetails });
                            }
                        }
                        callback(res);
                        return res;
                    });
                }
                else if (response.status === httpStatusCodes.UNAUTHORIZED) {
                    return response.json().then(json => {
                        res = {
                            status: httpStatusCodes.UNAUTHORIZED,
                            error: json
                        };

                        callback(res);
                        return res;
                    });
                }
                else if (response.status === httpStatusCodes.FORBIDDEN) {
                    return response.json().then(json => {
                        res = {
                            status: httpStatusCodes.FORBIDDEN,
                            error: json
                        };

                        callback(res);
                        return res;
                    });
                }

                throw new Error(response.message);
            })
            .catch(err => {
                res = {
                    status: httpStatusCodes.UNAUTHORIZED,
                    error: err
                };

                callback(res);

                return res;
            });
    }

    static getNotificationToken(token, callback = DEFAULT_CALLBACK) {
        const options = {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            }
        };

        return fetch(`/api/account/${token}`, options)
            .then(response => {
                if (response.status === httpStatusCodes.OK) {
                    return response.json().then(json => {
                        callback(json);
                        return json;
                    });
                }
                throw new Error(response.message);
            })
            .catch(err => {
                callback(err);

                return err;
            });
    }

    static loginWithUsername(username, password, loginWithEmailVerification, callback = DEFAULT_CALLBACK) {
        const props = {
            grant_type: 'password',
            username,
            password
        };

        if (loginWithEmailVerification) {
            props.require_verification = true;
        }
        return this.sendAccountDetails(props, callback);
    }

    static loginWithChallenger(token, callback = DEFAULT_CALLBACK) {
        return this.sendAccountDetails({
            grant_type: 'challenger',
            token
        }, callback);
    }

    static loginWithPrivateChallenger(hash, moderator, callback = DEFAULT_CALLBACK) {
        return this.sendAccountDetails({
            grant_type: 'moderator',
            hash,
            moderator
        }, callback);
    }

    static loginWithFacebook(accessToken, callback = DEFAULT_CALLBACK) {
        return this.sendAccountDetails({
            grant_type: 'facebook',
            token: accessToken
        }, callback);
    }

    static loginWithGoogle(token, callback = DEFAULT_CALLBACK) {
        return this.sendAccountDetails({
            grant_type: 'gplus',
            token
        }, callback);
    }

    // ------- Auth Logout ------------------
    static isLegionEnabled() {
        const loggedWithLegion = this.getCookie('lf') || this.getCookie('legionForced');

        return loggedWithLegion || false;
    }

    static logoutCallback = callback => res => {
        try {
            ChatModule.importChatWrapper().then(({ 'default': ChatWrapper }) => {
                const module = ChatWrapper.XMPP;

                if (module && typeof module.getConfig === 'function') {
                    return module.getInstance({ config: module.getConfig() }).then(instance => {
                        instance.logout();
                        return Auth.removeLogoutData(callback, res);
                    });
                }
                return Auth.removeLogoutData(callback, res);
            });
            return Auth.removeLogoutData(callback, res);
        }
        catch (e) {
            return Auth.removeLogoutData(callback, res);
        }
    };

    static clearLocalStorage = (excludedKeys, excludeRegexList) => {
        Object.keys(window.localStorage).forEach(key => {
            if (excludedKeys.indexOf(key) === -1 && !excludeRegexList.some(regex => regex.test(key))) {
                window.localStorage.removeItem(key);
            }
        });
    }

    static clearCookies = () => {
        COOKIES_TO_CLEAR.forEach(cookie => {
            cookieManagerHelper.eraseCookie(cookie);
        });
    }

    static clearIndexedDB = async () => {
        if (window.indexedDB) {
            const dbs = await window.indexedDB.databases();

            dbs.forEach(db => {
                // clearing only chat dbs as of now
                if (db.name === 'chat' || db.name === 'intelliChat') {
                    window.indexedDB.deleteDatabase(db.name);
                }
            });
        }
    }

    static removeLogoutData = (callback, res) => {
        if (typeof window !== 'undefined') {
            this.clearLocalStorage(KEYS_TO_PRESERVE, KEYS_TO_PRESERVE_REGEX);
            this.clearCookies();
            this.clearIndexedDB();
            window.location.href = '/account';
        }
        return callback(res);
    };

    static logout(callback = DEFAULT_CALLBACK) {
        const cb = this.logoutCallback(callback);

        removeItem(IS_SELLER_KYC_POPUP_SHOWN);
        clearLeadsDataFromStorage();
        clearEmiSelectedAdsFromStorage();
        window.localStorage.removeItem('_bifro_updatedtime_threads');
        if (Auth.isLegionEnabled()) {
            return this.logoutWithLegion(cb);
        }

        return this.logoutWithCore(cb);
    }

    static logoutAllDevices(callback = DEFAULT_CALLBACK) {
        const cb = this.logoutCallback(callback);

        removeItem(IS_SELLER_KYC_POPUP_SHOWN);
        window.localStorage.removeItem('_bifro_updatedtime_threads');

        return this.logoutAllDevicesWithLegion(cb);
    }

    static logoutWithCore = (callback = DEFAULT_CALLBACK) => {
        let res = {
            data: null,
            status: null,
            error: null
        };
        const options = {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const installation_id = getNotificationInstallationId();
        let url = logoutAPI;

        url += installation_id && `?installation_id=${installation_id}`;
        fetch(url, options)
            .then(response => {
                if (smartlockHelper.isCredentialManagementSupportedInBrowser()) {
                    smartlockHelper.preventSilentAccess();
                }

                if (response.status !== httpStatusCodes.OK) {
                    throw new Error(response.message);
                }

                return response.json();
            })
            .then(json => {
                res = {
                    data: json,
                    status: httpStatusCodes.OK
                };
                return callback(res);
            }, err => {
                res = {
                    status: httpStatusCodes.UNAUTHORIZED,
                    error: err
                };
                callback(res);
            });
    };

    static logoutWithLegion = (callback = DEFAULT_CALLBACK) => {
        let options = {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const sitecode = this.getCookie('sitecode');

        if (sitecode) {
            options.headers['x-panamera-sitecode'] = sitecode;
        }

        const url = legionLogoutAPI;

        if (typeof window !== 'undefined' && window.localStorage.getItem('subscriptionId')) {
            options = {
                ...options,
                headers: {
                    ...options.headers,
                    'x-installation-id': window.localStorage.getItem('subscriptionId')
                }
            };
        }

        fetch(url, options)
            .then(response => {
                if (smartlockHelper.isCredentialManagementSupportedInBrowser()) {
                    smartlockHelper.preventSilentAccess();
                }

                if (response.status !== httpStatusCodes.OK) {
                    throw new Error(response.message);
                }
            })
            .then(() => {
                const res = {
                    data: {},
                    status: httpStatusCodes.OK
                };

                if (this.getCookie('legionForced')) {
                    cookieManagerHelper.eraseCookie('legionForced');
                }
                return callback(res);
            }, err => {
                const res = {
                    status: httpStatusCodes.UNAUTHORIZED,
                    error: err
                };

                callback(res);
            });
    };

    static logoutAllDevicesWithLegion = callback => {
        let options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const url = '/api/auth/logout-all';

        if (typeof window !== 'undefined' && window.localStorage.getItem('subscriptionId')) {
            options = {
                ...options,
                headers: {
                    ...options.headers,
                    'x-installation-id': window.localStorage.getItem('subscriptionId')
                }
            };
        }

        fetch(url, options)
            .then(response => {
                if (smartlockHelper.isCredentialManagementSupportedInBrowser()) {
                    smartlockHelper.preventSilentAccess();
                }

                if (response.status !== httpStatusCodes.OK) {
                    throw new Error(response.message);
                }
            })
            .then(() => {
                const res = {
                    data: {},
                    status: httpStatusCodes.OK
                };

                return callback(res);
            }, err => {
                const res = {
                    status: httpStatusCodes.UNAUTHORIZED,
                    error: err
                };

                callback(res);
            });
    };

    static isLoggedIn() {
        return !!Auth.getUser();
    }

    static requiresAuth(state, replace) {
        if (!Auth.isLoggedIn()) {
            let search = Object.assign({}, state.location.query);

            if (search.login_method) {
                delete search.login_method;
            }
            if (search.login_value) {
                delete search.login_value;
            }
            if (search.cancelRedirect) {
                delete search.cancelRedirect;
            }

            search = urlHelper.paramsToQueryString(search);
            let to;

            if (search) {
                to = `${state.location.pathname}?${search}`;
            }
            else {
                to = `${state.location.pathname}`;
            }
            const query = Object.assign({}, { to }, state.location.query);

            replace({
                pathname: `${getLanguageBasePath(state.params)}/account`,
                search: `?${urlHelper.paramsToQueryString(query)}`
            });
        }
    }

    static getCookie(name) {
        return cookieManagerHelper.readCookie(name);
    }

    static getUser() {
        if (typeof document === 'undefined') {
            return Auth.user;
        }

        try {
            return JSON.parse(decodeURIComponent(this.getCookie('user')).substr(2));
        }
        catch (e) {
            return null;
        }
    }

    static setChatToken(token, userId, cb) {
        parser.encrypt(token, `#${userId}#`).then(encryptedToken => {
            window.localStorage.setItem('__ct__bifro_ct', encryptedToken);
            if (typeof cb === 'function') {
                return cb(true);
            }

            return undefined;
        });
    }

    static setChatTokenInLocalStorage = (userId, callback) => {
        const chat_token = cookieManagerHelper.readCookie('ct');

        if (!chat_token && !window.localStorage) {
            return Promise.resolve();
        }
        return Auth.setChatToken(chat_token, userId, callback);
    };

    static getLoginMethod(userName) {
        if (userName.search('@') > 0) {
            return 'email';
        }

        return 'phone';
    }

    static regenerateToken(cb) {
        window.newrelic?.addPageAction('chat_regenerate_token');
        return cb(this.getCookie('ct'));
    }

    static fetchToken() {
        window.newrelic.addPageAction('chat_fetch_token');
        return Promise.resolve(this.getCookie('ct'));
    }

    static setCookie(name, value) {
        // eslint-disable-next-line no-useless-catch
        try {
            if (typeof value === 'object') {
                value = encodeURIComponent(JSON.stringify(value));
            }
            cookieManagerHelper.createCookie(name, value, 1);
        }
        catch (e) {
            throw (e);
        }
    }
}

export default Auth;

